import type { ActiveSession } from "./types";

/**
 * A session in progress, as arithmetic over the record the store keeps for it.
 *
 * The record exists because the player screens used to hold their clock in component state and,
 * for the walk, in the synced blob — which the merge rebuilds field by field and so silently
 * erased a few seconds after Start. Reopening the app then began a *second* session while the
 * phone's counter was still holding the first. Everything a screen needs to pick up where it left
 * off lives here, and every figure is derived from wall-clock stamps rather than accumulated
 * ticks, because a backgrounded tab does not tick. (ADR-027)
 */

export const SESSION_LABEL: Record<ActiveSession["kind"], string> = {
  walk: "Walk",
  treadmill: "Treadmill",
  workout: "Session",
};

/** Milliseconds the session has been running, paused stretches excluded. */
export function sessionElapsedMs(s: ActiveSession, now: number): number {
  if (s.kind === "treadmill") return treadmillElapsedMs(s, now);
  return s.banked + (s.since !== null ? Math.max(0, now - s.since) : 0);
}

export interface TreadmillPosition {
  idx: number;
  /** ms left in that phase */
  leftMs: number;
  /** every phase ended while the page was away */
  over: boolean;
}

/**
 * Where a treadmill session has got to, given when its current phase was due to end.
 *
 * The machine does not stop because the page went away, so if the deadline has passed the
 * session moved on to the next phase at that moment, and the one after that at its own — the
 * position is walked forward phase by phase rather than restarted from the top.
 */
export function treadmillPosition(s: ActiveSession, now: number): TreadmillPosition {
  const phases = s.phases ?? [];
  if (!phases.length) return { idx: 0, leftMs: 0, over: true };
  let idx = Math.min(Math.max(0, s.idx ?? 0), phases.length - 1);

  if (s.since === null) {
    return { idx, leftMs: s.phaseLeftMs ?? phases[idx].secs * 1000, over: false };
  }

  let endsAt = s.phaseEndsAt ?? s.since + phases[idx].secs * 1000;
  while (now >= endsAt) {
    if (idx + 1 >= phases.length) return { idx, leftMs: 0, over: true };
    idx++;
    endsAt += phases[idx].secs * 1000;
  }
  return { idx, leftMs: endsAt - now, over: false };
}

export function treadmillElapsedMs(s: ActiveSession, now: number): number {
  const phases = s.phases ?? [];
  const pos = treadmillPosition(s, now);
  let ms = 0;
  for (let k = 0; k < pos.idx; k++) ms += phases[k].secs * 1000;
  const cur = phases[pos.idx];
  if (cur) ms += cur.secs * 1000 - pos.leftMs;
  return ms;
}

/**
 * When a running treadmill session is due to end, every remaining phase included.
 *
 * Null while paused — a paused session has no end until it is resumed. This is the honest end
 * time for a session that ran its course while the page was away: logging it at the moment the
 * app reopened would put the whole evening inside its measured window.
 */
export function treadmillEndsAt(s: ActiveSession): number | null {
  const phases = s.phases ?? [];
  if (s.since === null || !phases.length) return null;
  const idx = Math.min(Math.max(0, s.idx ?? 0), phases.length - 1);
  let ends = s.phaseEndsAt ?? s.since + phases[idx].secs * 1000;
  for (let k = idx + 1; k < phases.length; k++) ends += phases[k].secs * 1000;
  return ends;
}

/**
 * How old a record may be and still be resumed.
 *
 * The same three hours as the counter app's own limit: nobody walks for that long without also
 * finishing the walk, and a record older than it is a session somebody forgot, not one to land
 * on at the next launch — a rescue left open yesterday must not make today's session a rescue.
 */
export const SESSION_MAX_AGE_MS = 3 * 60 * 60 * 1000;

export function sessionStale(s: ActiveSession, now: number): boolean {
  const started = Date.parse(s.startedAt);
  return !Number.isFinite(started) || now - started > SESSION_MAX_AGE_MS;
}

/** The path a route lives at, without its query — enough to tell "already on it" apart. */
export function routePath(route: string): string {
  const q = route.indexOf("?");
  return q === -1 ? route : route.slice(0, q);
}
